import React, { useContext, useEffect, useState } from 'react';
import { ThemeContext } from '../../context/ThemeContext';
import { DefaultTokenContext } from '../../context/DefaultTokenContext';

import { Copy } from 'lucide-react';
import CustomButton from '../Componentization/CustomButton';
import CustomText from '../../styles/CustomText';
import MontserratText from '../../styles/MontserratText';
import useWallet from '../../hooks/useWallet';
import useSecureStorage from '../../hooks/useSecureStorage';
import { CopyToClipboard } from 'react-copy-to-clipboard';

import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { checkMnemonic, RootState } from '../../store';

import i18next from 'i18next'

const SecretPhraseConfirmation: React.FC = () => {
	const { theme } = useContext(ThemeContext)
	let navigate = useNavigate();
	const { t } = useTranslation();

	const { makeWallet, restoreWallet } = useWallet()
	const { 
		storeNetworkEncryptedData,
		setLocalMnemonicHash,
		setBlockChain, 
		setNetworkCurrency, 
		setWalletAddress, 
		setNetworkWalletAddress,
		overwriteWalletData,
		logOut 
	} = useSecureStorage()

	const { getNetworkInfo } = useContext(DefaultTokenContext);

	const [mnemonic, setMnemonic] = useState<string[]>([])
	const [loading, setLoading] = useState<boolean>(true)
	const [isComplate, setIsComplate] = useState<boolean>(false)

	const pincode = useSelector((state: RootState) => state.pincode.value);

	useEffect(() => {
		initializeWallet()
	}, [])

	const initializeWallet = async () => {
		try {
			// 각 네트워크 지갑 생성시 공유할 니모닉코드 생성
			const { privateKey, publicKey, address, mnemonic } = await makeWallet(process.env.REACT_APP_NETWORK_NAME!)

			// 지갑이 정상적으로 생성됐는지 확인
			if (null == privateKey || null == publicKey || null == address || null == mnemonic) {
				setIsComplate(false)
				alert('Error Make base Mnemonic. please restart');
				navigate('/CreateWallet')
				return
			}

			console.log(`SecretPhraseConfirmation initializeWallet baseMnemonic ${mnemonic}`)

			// 네트워크 설정 정보
			const defaultNetworks = await getNetworkInfo('')
			console.log(`defaultNetworks : `, defaultNetworks)
			const activeNetworks = defaultNetworks.filter(network => network.is_active);
			console.log(`활성화된 네트워크 목록:`, activeNetworks);

			// 활성화된 네트워크 별로 지갑 생성 및 저장
			let walletResults: any[] = [];
			for (const network of activeNetworks) {
				const result = await saveNetworkWallet(network.symbol, mnemonic, pincode);
				if (result.result === false || result.address == null) {
					console.error(`❌ ${network.symbol} 지갑 복구 실패`);
					setIsComplate(false)
					logOut();
					alert('Error Make mnemonic. please restart');
					navigate(`/CreateWallet`, { replace: true, state: { } });
					return
				}
				
				// 네트워크 지갑 주소 저장
				await setNetworkWalletAddress(result.address, network.symbol);
				walletResults.push({ network: network.symbol, address: result.address });
			}
			
			// 기본 네트워크 선택 및 저장
			const primaryNetwork = activeNetworks.find(net => net.symbol === process.env.REACT_APP_NETWORK_NAME);
			if (!primaryNetwork) {
				console.error('❌ 기본 네트워크 설정 실패');
				logOut();
				alert('Error setting default network wallet. Please restart.');
				navigate(`/CreateWallet`, { replace: true });
				return;
			}

			// 로컬 스토리지에 기본 네트워크 설정
			await overwriteWalletData(primaryNetwork.symbol);

			// 로컬 스토리지에 기본 지갑주소 설정
			await setWalletAddress(walletResults.find(w => w.network === primaryNetwork.symbol)?.address);

			// 현재 기본 블록체인 저장
			await setBlockChain(primaryNetwork.symbol);

			// 현재 기본 네트워크의 메인 currency 저장
			await setNetworkCurrency(primaryNetwork.currency);

			// 로컬 니모닉 해시 저장
			await setLocalMnemonicHash(mnemonic);

			
			setMnemonic(mnemonic.split(' '))
			setIsComplate(true)

		} catch (error) {
			console.error('Error initializing wallet:', error)
			setIsComplate(false)
			logOut();
			alert('Error Make wallet. please restart');
			navigate(`/CreateWallet`, { replace: true, state: { } });
		} finally {
			setLoading(false)
		}
	}

	/**
	 * 네트워크 지갑 생성 및 저장
	 * @param network 
	 * @param mnemonic 
	 * @param pinCode 
	 */

	const saveNetworkWallet = async (
		network: string, 
		mnemonic: string, 
		pinCode: string
	) => {
		try {
			const { privateKey, publicKey, address } = await restoreWallet(mnemonic, network)

			if (network == null || address == null|| null == privateKey || null == publicKey || null == mnemonic) {
				return {privateKey:null, publicKey:null, address:null, mnemonic:null, result:false}
			}

			const result = await storeNetworkEncryptedData(network, privateKey, publicKey, mnemonic, pinCode);
			if(false === result) {
				return {privateKey:null, publicKey:null, address:null, mnemonic:null, result:false}
			}

			return {privateKey, publicKey, address, mnemonic, result:true} 
		} catch (error) {
			console.error('Error save network wallet:', error)
		}
		return {privateKey:null, publicKey:null, address:null, mnemonic:null, result:false} 
	}

	const handleNext = () => {
		if (true === isComplate) {
			navigate(`/SecretPhraseCheck`, { replace: true, state: { } });
		} else {
			alert('Error Make mnemonic. please restart');
		}
	}

	const handleCopy = (): void => {
		const code = mnemonic.join(' ')
		if (code) {
			navigator.clipboard.writeText(code)
				.then(() => {
					alert(i18next.t('verify_recovery_syntax_message_10'));
				})
				.catch(err => {
					console.error('Fail Copy mnemonic:', err);
				});
		}
	};

	const styles = {
		scrollContainer: {
			width: "100%",
		} as React.CSSProperties,
		header: {
			marginBottom: "30px"
		} as React.CSSProperties,
		body: {
			marginBottom: "40px"
		} as React.CSSProperties,
		title: {
			fontSize: 24,
			textAlign: 'left',
			marginBottom: 10
		} as React.CSSProperties,
		subTitle: {
			fontSize: 14,
			textAlign: 'left',
			marginBottom: 5
		} as React.CSSProperties,
		phraseContainer: {
			display: "grid",
			gridTemplateColumns: "repeat(2, 1fr)",
			gap: "10px"
		} as React.CSSProperties,
		phraseItem: {
			marginBottom: 8,
			display: "flex",
			alignItems: 'center',
			padding: 8,
			borderRadius: 31
		} as React.CSSProperties,
		phraseIndexContainer: {
			padding: 5,
			paddingTop: 1,
			paddingBottom: 1,
			marginRight: 5
		} as React.CSSProperties,
		phraseIndex: {
			fontSize: 18,
			height: "20px",
			display: "flex",
			alignItems: "center"
		} as React.CSSProperties,
		phraseWord: {
			fontSize: 18,
			paddingLeft: 4,
			height: "20px",
			display: "flex",
			alignItems: "center"
		} as React.CSSProperties,
		clipboardButton: {
			display: "flex",
			alignItems: 'center',
			justifyContent: 'center',
			gap: "10px",
			borderRadius: 10,
			marginTop: 10,
			height: 48,
			cursor: "pointer"
		} as React.CSSProperties,
		clipboardButtonText: {
			fontSize: 14
		} as React.CSSProperties,
		warning: {
			marginTop: 15,
			display: "flex",
			alignItems: 'center',
			justifyContent: 'flex-start'
		} as React.CSSProperties,
		warningText1: {
			fontSize: 16,
		} as React.CSSProperties,
		warningText2: {
			fontSize: 14,
			textAlign: 'left',
			marginTop: 5
		} as React.CSSProperties,
		warningText3: {
			fontSize: 14,
			textAlign: 'left',
			marginTop: 1
		} as React.CSSProperties,
		footer: {
			display: "flex",
			justifyContent: 'flex-end',
		} as React.CSSProperties,
		button: {
			width: "100%",
			display: "flex",
			padding: 15,
			borderRadius: 10,
			alignItems: 'center',
			height: 54,
			justifyContent: 'center'
		} as React.CSSProperties,
		buttonText: {
			color: theme.static_white,
			fontSize: 16
		} as React.CSSProperties,
		icon: {
			width: 18,
			height: 18,
			marginRight: 5
		} as React.CSSProperties
	}

	return (
		<div className='container' style={{ backgroundColor: theme.background_normal }}>
			<div style={styles.scrollContainer}>
				<div style={styles.header}>
					<CustomText weight="medium" style={{ ...styles.title, color: theme.label_strong }}>
						{t('verify_recovery_syntax_message_01')}
					</CustomText>
					<CustomText style={{ ...styles.subTitle, color: theme.label_alternative }}>
						{t('verify_recovery_syntax_message_02')}
					</CustomText>
					<CustomText style={{ ...styles.subTitle, color: theme.label_alternative }}>
						{t('verify_recovery_syntax_message_03')}
					</CustomText>
					<CustomText style={{ ...styles.subTitle, color: theme.label_alternative }}>
						{t('verify_recovery_syntax_message_04')}
					</CustomText>
				</div>

				<div style={styles.body}>
					<div>
						{loading ? (
							<div style={styles.phraseContainer}>
								{Array.from({ length: 12 }).map((_, index) => (
									<div key={index} style={{ ...styles.phraseItem, border: `1px solid ${theme.line_alternative}` }}>
										<div style={styles.phraseIndexContainer}>
											<MontserratText weight="medium" style={{ ...styles.phraseIndex, color: theme.primary_normal }}>
												{index + 1}
											</MontserratText>
										</div>
										<MontserratText weight="medium" style={{ ...styles.phraseWord, color: theme.label_normal }}>
											????
										</MontserratText>
									</div>
								))}
							</div>
						) : (
							<div style={styles.phraseContainer}>
								{
									mnemonic.map((word, index) => (
										<div key={index} style={{ ...styles.phraseItem, border: `1px solid ${theme.line_alternative}` }}>
											<div style={styles.phraseIndexContainer}>
												<MontserratText weight="medium" style={{ ...styles.phraseIndex, color: theme.primary_normal }}>
													{index + 1}
												</MontserratText>
											</div>
											<MontserratText weight="medium" style={{ ...styles.phraseWord, color: theme.label_normal }}>
												{word}
											</MontserratText>
										</div>
									))
								}
							</div>
						)}
					</div>

					<CopyToClipboard text={mnemonic.join(' ')} onCopy={handleCopy}>
						<div style={{ ...styles.clipboardButton, ...styles.clipboardButtonText, border: `1px solid ${theme.primary_normal}`, color: theme.primary_normal, backgroundColor: theme.fill_neutral }}>
							<Copy size={15} />
							<div>{t('verify_recovery_syntax_message_05')}</div>
						</div>
					</CopyToClipboard>

					<div style={styles.warning}>
						<img
							src="./img/mark.png" // 웹에서 사용할 이미지 경로로 변경
							alt="Warning"
							style={styles.icon}
						/>
						<CustomText weight="medium" style={{ ...styles.warningText1, color: theme.label_strong }}>
							{t('verify_recovery_syntax_message_06')}
						</CustomText>
					</div>

					<CustomText style={{ ...styles.warningText2, color: theme.label_alternative }}>
						{t('verify_recovery_syntax_message_07')}
					</CustomText>
					<CustomText style={{ ...styles.warningText3, color: theme.label_alternative }}>
						{t('verify_recovery_syntax_message_08')}
					</CustomText>
				</div>

				<div style={styles.footer}>
					<CustomButton
						containerStyle={{ ...styles.button, backgroundColor: theme.primary_normal }}
						textStyle={styles.buttonText}
						onPress={handleNext}
						text={t('verify_recovery_syntax_message_09')}
						withBackground={true}
						backgroundColor={theme.primary_normal}
					/>
				</div>
			</div>
		</div>
	)
}

export default SecretPhraseConfirmation
